import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUserOnlineStatus } from "@/hooks/usePresence";
import type { Conversation } from "@/pages/Messages";
import type { Tables } from "@/integrations/supabase/types";
import { normaliseLoginId } from "@/lib/loginId";
import { otherParty } from "@/lib/directMessages";

const MAX_SIDEBAR_CONVERSATIONS = 5;

export function useSidebarConversations() {
  const { user } = useAuth();

  // Reuse same query keys as Messages page for cache sharing
  const { data: messages = [] } = useQuery({
    queryKey: ["messages", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("messages")
        .select("id, sender_id, recipient_id, sender_name, content, read, created_at, updated_at, conversation_id, message_type, attachments")
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data || []) as Tables<"messages">[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const otherUserIds = useMemo(() => {
    if (!user?.id || messages.length === 0) return [];
    const ids = new Set<string>();
    messages.forEach((m) => {
      const { otherLoginId } = otherParty(m, user.id);
      if (otherLoginId) ids.add(otherLoginId);
    });
    return Array.from(ids);
  }, [messages, user?.id]);

  const { data: otherProfiles = [] } = useQuery({
    queryKey: ["message-profiles", user?.id, otherUserIds.join(",")],
    queryFn: async () => {
      if (otherUserIds.length === 0) return [];
      const { data, error } = await supabase
        .from("profiles")
        // sender_id / recipient_id are LOGIN ids, so look people up by
        // profiles.user_id; .in("id", ...) matched the profile PK and found
        // nobody (HDQ-WEB-ID-1).
        .select("user_id, full_name, email")
        .in("user_id", otherUserIds);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && otherUserIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const profileById = useMemo(() => {
    const byLoginId = new Map<string, (typeof otherProfiles)[number]>();
    for (const p of otherProfiles) {
      const loginId = normaliseLoginId(p.user_id);
      if (loginId) byLoginId.set(loginId, p);
    }
    return byLoginId;
  }, [otherProfiles]);

  const { data: conversationStates = [] } = useQuery({
    queryKey: ["conversation-states", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("conversation_states")
        .select("id, user_id, conversation_id, is_pinned, is_typing, last_read_at")
        .eq("user_id", user.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  const recipientIds = useMemo(() => {
    if (!user?.id || !messages.length) return [];
    const ids = new Set<string>();
    messages.forEach((msg) => {
      const { otherLoginId } = otherParty(msg, user.id);
      if (otherLoginId) ids.add(otherLoginId);
    });
    return Array.from(ids);
  }, [messages, user?.id]);

  const { getUserStatus } = useUserOnlineStatus(recipientIds);

  const conversations: Conversation[] = useMemo(() => {
    if (!user?.id || !messages.length) return [];

    const convMap = new Map<string, Conversation>();

    messages.forEach((msg) => {
      const { otherLoginId, iAmRecipient: isRecipient } = otherParty(msg, user.id);
      const otherUserId = otherLoginId ?? "";
      const otherProfile = profileById.get(otherUserId);
      const otherUserName = otherProfile?.full_name ?? msg.sender_name ?? "User";
      const otherUserEmail = otherProfile?.email ?? "";

      if (!convMap.has(msg.conversation_id)) {
        const state = conversationStates.find(
          (s) => s.conversation_id === msg.conversation_id,
        );
        const { isOnline } = getUserStatus(otherUserId);

        convMap.set(msg.conversation_id, {
          id: msg.conversation_id,
          recipientId: otherUserId,
          recipientName: otherUserName,
          recipientEmail: otherUserEmail,
          lastMessage: msg.content || (Array.isArray(msg.attachments) && msg.attachments.length ? "Attachment" : ""),
          lastMessageTime: msg.created_at,
          unreadCount: 0,
          isPinned: state?.is_pinned || false,
          isOnline,
        });
      }

      if (isRecipient && !msg.read) {
        const conv = convMap.get(msg.conversation_id)!;
        conv.unreadCount++;
      }
    });

    return Array.from(convMap.values())
      .sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
      })
      .slice(0, MAX_SIDEBAR_CONVERSATIONS);
  }, [messages, conversationStates, user?.id, getUserStatus, profileById]);

  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return { conversations, totalUnread };
}
